import { useLang } from '../lib/lang';
import InlineEditable from './InlineEditable';
import WinnerPhotoCarousel from './WinnerPhotoCarousel';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Star, Quote } from 'lucide-react';

// Shared premium winner card (cinematic). Used on HomePage section + WinnersPage.
export default function WinnerCard({ w, showDate = false }) {
  const { t } = useLang();
  const photos = (w.photos && w.photos.length ? w.photos : (w.images || []));

  return (
    <div className="group relative rounded-[1.75rem] overflow-hidden border border-gold/15 bg-rolex-green-deep shadow-[0_30px_70px_-25px_rgba(0,0,0,0.75)] h-full transition-all duration-500 hover:border-gold/40 hover:shadow-[0_40px_90px_-20px_rgba(184,138,42,0.35)]">
      <div className="relative aspect-[4/5] overflow-hidden">
        <WinnerPhotoCarousel photos={photos} alt={w.raffle_title} className="absolute inset-0" imgClassName="transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
        {/* Stronger bottom shade for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent pointer-events-none" />

        {/* Verified badge */}
        <div className="absolute top-4 left-4 z-20 inline-flex items-center gap-1 rounded-full bg-gold-gradient text-rolex-green px-3 py-1 text-[10px] font-bold uppercase tracking-wider shadow-lg">
          <Star className="w-3 h-3" /> <InlineEditable tKey="verifiedWinner">{t('verifiedWinner')}</InlineEditable>
        </div>

        {/* Diagonal corner TICKET ribbon (top-right) */}
        {w.winning_ticket_code && (
          <div className="pointer-events-none absolute -right-px -top-px z-20 h-32 w-32 overflow-hidden">
            <div className="absolute right-[-46px] top-[26px] w-[188px] rotate-45 bg-gold-gradient shadow-[0_6px_16px_rgba(0,0,0,0.5)] border-y-2 border-rolex-green/25">
              <div className="flex flex-col items-center py-2">
                <span className="text-[9px] font-extrabold uppercase tracking-[0.22em] text-rolex-green/80 leading-none">🎟 {t('ticket') || 'Ticket'}</span>
                <span className="border-t border-dashed border-rolex-green/50 w-28 my-1" />
                <span className="font-mono text-[12px] font-black tracking-wider text-rolex-green leading-none">#{w.winning_ticket_code}</span>
              </div>
            </div>
          </div>
        )}

        {/* Identity overlay */}
        <div className="absolute bottom-0 inset-x-0 p-5 z-20 pointer-events-none [text-shadow:0_2px_8px_rgba(0,0,0,0.85)]">
          <div className="flex items-end justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <Avatar className="w-10 h-10 border-2 border-gold/60 shrink-0 shadow-lg"><AvatarFallback className="bg-gold-gradient text-rolex-green text-sm font-bold">{w.user_name?.charAt(0)}</AvatarFallback></Avatar>
              <div className="min-w-0">
                <p className="font-display font-extrabold text-cream text-base leading-tight truncate">{w.user_name}</p>
                <p className="text-xs text-cream/80 truncate"><InlineEditable tKey="won">{t('won')}</InlineEditable> {w.raffle_title}</p>
              </div>
            </div>
            <p className="font-display font-black text-2xl text-gold shrink-0">£{w.product_value?.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Testimonial — the comment left by the winner */}
      <div className="p-5 bg-rolex-green-deep">
        {w.testimonial ? (
          <div className="relative rounded-2xl bg-cream/[0.05] border border-gold/15 p-4 pt-5" data-testid="winner-testimonial">
            <Quote className="absolute -top-3 left-4 w-6 h-6 text-gold fill-gold/30" />
            <p className="text-sm text-cream/85 italic leading-relaxed line-clamp-4">{w.testimonial}</p>
            <div className="mt-3 flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.18em] text-gold/70 font-bold">— {w.user_name}</p>
              {showDate && w.drawn_at && <p className="text-[10px] text-cream/40">{new Date(w.drawn_at).toLocaleDateString()}</p>}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-cream/15 p-4 text-center">
            <p className="text-[11px] text-cream/40 italic"><InlineEditable tKey="testimonialAwaiting">{t('testimonialAwaiting') || 'Témoignage à venir...'}</InlineEditable></p>
          </div>
        )}
      </div>
    </div>
  );
}
